import React, { useState } from 'react'
import { View, TouchableOpacity, StyleProp, ViewStyle } from 'react-native'
import { Divider, MenuItem, OverflowMenu } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon } from '@components/base'

export interface MenuInfoItem {
  title: string
  isEnabled: boolean
  // Receives a `close` callback that dismisses the menu. Items that open a confirmation
  // sheet / alert should set `deferClose` and call `close` once that sheet is dismissed,
  // so the menu's native Modal isn't torn down while the sheet is being presented on iOS
  // (ENG-4630). Items that navigate can ignore `close`; the menu closes them immediately.
  onPress: (close: () => void) => void
  accent?: boolean
  deferClose?: boolean
}

export interface MenuProps {
  menuItems?: MenuInfoItem[]
  menuEnabled?: boolean
  menuButtonStyle?: StyleProp<ViewStyle>
}

export const Menu = ({ menuEnabled = true, menuItems, menuButtonStyle }: MenuProps) => {
  const styles = useStyleSheet(themedStyle)

  const [menuVisible, setMenuVisible] = useState(false)

  if (!menuItems || menuItems.length === 0) {
    return null
  }

  const renderMenuButton = () => (
    <TouchableOpacity
      testID="OverflowMenu/More"
      style={[styles.menuButton, menuButtonStyle]}
      activeOpacity={0.7}
      disabled={!menuEnabled}
      accessibilityLabel="OverflowMenu/More"
      onPress={() => setMenuVisible(true)}
    >
      <Icon name="dots-three" />
    </TouchableOpacity>
  )

  return (
    <OverflowMenu
      style={styles.overflowMenu}
      appearance="noDivider"
      anchor={renderMenuButton}
      visible={menuVisible}
      onBackdropPress={() => setMenuVisible(false)}
    >
      <View style={styles.container}>
        {menuItems.map((item: MenuInfoItem, index: number) => {
          const onPress = () => {
            const close = () => setMenuVisible(false)
            item.onPress(close)
            // Items that present a confirmation sheet keep the menu mounted and call
            // `close` themselves once the sheet is dismissed; everything else (e.g.
            // navigation) closes the menu right away.
            if (!item.deferClose) {
              close()
            }
          }

          const menuItem = (
            <MenuItem
              testID={item.title}
              title={item.title}
              disabled={!item.isEnabled}
              onPress={onPress}
              accessibilityLabel={item.title}
              appearance={item.accent ? 'overflowMenuAccent' : 'overflowMenu'}
            />
          )

          // have to do it this way rather than with a conditional divider because
          // having a null inside a mapped Fragment leads to some errors
          if (index === 0) {
            return <View key={index}>{menuItem}</View>
          }

          return (
            <View key={index}>
              <Divider />
              {menuItem}
            </View>
          )
        })}
      </View>
    </OverflowMenu>
  )
}

const themedStyle = StyleService.create({
  overflowMenu: {
    borderWidth: 0,
    borderRadius: 8,
    zIndex: 10,
  },
  container: {
    overflow: 'hidden',
    borderWidth: 1,
    borderRadius: 8,
    borderColor: 'theme.surface.base',
  },
  menuButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
})
